// Veřejný přehled publikovaných Fokus seznamů + vstup do průvodce a moje seznamy.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crosshair, Plus, PencilSimple, Trash, Trophy } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { focusListsPublic, focusListsMine, focusListDelete } from "../lib/focus-api";
import PublishFocusList from "../components/lab/PublishFocusList";

const I18N = {
    cs: {
        heading: "Fokus seznamy",
        desc: "Seznamy hráčů ke sledování od manažerů komunity — podložené AI scoutingem a modelem. Hlasuj o picích, sestav vlastní seznam a sbírej reputaci za trefy (autor +2 za trefený pick, hlasující +5 za správný názor).",
        newList: "Nový Fokus seznam", loginCta: "Přihlas se a vytvoř vlastní", mine: "Moje seznamy", week: "tento týden",
        loading: "Načítám…", empty: "Zatím žádný publikovaný seznam — buď první!", players: "hráčů", open: "otevřít",
        published: "Publikováno", draft: "Koncept", rep: "rep", hit: "trefeno",
    },
    en: {
        heading: "Focus lists",
        desc: "Player watchlists curated by community managers — backed by AI scouting and our model. Vote on picks, build your own list and earn reputation for correct calls (author +2 per hit, voters +5 for a correct opinion).",
        newList: "New Focus list", loginCta: "Sign in to create your own", mine: "My lists", week: "this week",
        loading: "Loading…", empty: "No published lists yet — be the first!", players: "players", open: "open",
        published: "Published", draft: "Draft", rep: "rep", hit: "hits",
    },
};

function MyLists({ refresh, onEdit }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        focusListsMine().then(setData).catch(() => {});
    }, [refresh]);
    if (!data?.lists?.length) return null;
    const remove = async (l) => {
        try { await focusListDelete(l.id); setData({ ...data, lists: data.lists.filter((x) => x.id !== l.id) }); toast.success("Smazáno"); }
        catch { toast.error("Smazání selhalo"); }
    };
    return (
        <section className="space-y-2" data-testid="focus-my-lists">
            <div className="flex items-center gap-2">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Moje seznamy</p>
                <span className="font-mono text-[9px] text-white/35 ml-auto">{data.weekly_used}/{data.weekly_limit} tento týden</span>
            </div>
            {data.lists.map((l) => (
                <div key={l.id} className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2 flex-wrap" data-testid={`focus-my-row-${l.id}`}>
                    <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${l.status === "published" ? "border-fa-mint/60 text-fa-mint" : "border-fa-yellow/50 text-fa-yellow"}`}>
                        {l.status === "published" ? "Publikováno" : "Koncept"}
                    </span>
                    <span className="font-display font-bold text-sm">{l.title}</span>
                    {l.author_score && (
                        <span className="font-mono text-[9px] text-fa-yellow inline-flex items-center gap-1"><Trophy size={10} /> {l.author_score.hits}/{l.author_score.total} · +{l.author_score.rep_awarded} rep</span>
                    )}
                    {l.status === "published" && <Link to={`/fokus/${l.slug}`} className="font-mono text-[9px] text-fa-sky underline">otevřít</Link>}
                    <div className="ml-auto flex gap-2">
                        <button type="button" onClick={() => onEdit(l)} className="text-fa-sky" data-testid={`focus-my-edit-${l.id}`}><PencilSimple size={13} /></button>
                        <button type="button" onClick={() => remove(l)} className="text-white/30 hover:text-red-400" data-testid={`focus-my-delete-${l.id}`}><Trash size={13} /></button>
                    </div>
                </div>
            ))}
        </section>
    );
}

export default function FocusLists() {
    const { user, login } = useAuth();
    const { lang } = useLang();
    const T = I18N[lang] || I18N.cs;
    const [lists, setLists] = useState(null);
    const [editing, setEditing] = useState(null);
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        document.title = "Fokus seznamy — kurátorované transfer targets | Fantasy Academy";
        focusListsPublic().then((d) => setLists(d.lists || [])).catch(() => setLists([]));
    }, [refresh]);

    return (
        <AppShell>
            <div className="max-w-4xl mx-auto space-y-6" data-testid="focus-lists-page">
                <header className="border border-fa-yellow/25 bg-gradient-to-br from-fa-yellow/10 via-transparent to-transparent p-6 md:p-8">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <Crosshair size={12} weight="fill" /> Recruitment
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-2">{T.heading}</h1>
                    <p className="font-mono text-[11px] text-white/60 mt-3 max-w-2xl leading-relaxed">{T.desc}</p>
                    {user ? (
                        <Link to="/fokus/novy" className="inline-flex items-center gap-1.5 mt-4 font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 bg-fa-yellow text-black font-bold hover:bg-fa-mint transition-colors" data-testid="focus-new-list-btn">
                            <Plus size={12} weight="bold" /> {T.newList}
                        </Link>
                    ) : (
                        <button onClick={login} className="inline-flex items-center gap-1.5 mt-4 font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow hover:text-black transition-colors" data-testid="focus-login-btn">
                            <Plus size={12} weight="bold" /> {T.loginCta}
                        </button>
                    )}
                </header>

                {user && <MyLists refresh={refresh} onEdit={setEditing} />}
                {editing && <PublishFocusList list={editing} onClose={() => { setEditing(null); setRefresh((x) => x + 1); }} />}

                {lists === null && <p className="font-mono text-[11px] text-white/40">{T.loading}</p>}
                {lists?.length === 0 && <p className="font-mono text-[11px] text-white/40" data-testid="focus-lists-empty">{T.empty}</p>}
                <div className="grid md:grid-cols-2 gap-3">
                    {(lists || []).map((l) => (
                        <Link key={l.id} to={`/fokus/${l.slug}`} data-testid={`focus-list-card-${l.slug}`}
                              className="border border-white/10 bg-black/40 p-4 space-y-2 hover:border-fa-yellow/50 transition-colors block">
                            <div className="font-mono text-[8px] uppercase tracking-widest text-white/40">
                                {l.published_at ? new Date(l.published_at).toLocaleDateString(lang === "en" ? "en-GB" : "cs") : ""} · {l.player_count} {T.players}
                                {l.criteria?.gw_start && <> · GW{l.criteria.gw_start}–{l.criteria.gw_end}</>}
                            </div>
                            <div className="font-display font-black uppercase text-lg leading-tight">{l.title}</div>
                            {l.intro && <p className="font-mono text-[10px] text-white/55 leading-relaxed line-clamp-2">{l.intro}</p>}
                            <div className="flex items-center gap-2 font-mono text-[9px] text-white/50 flex-wrap" data-testid={`focus-card-author-${l.slug}`}>
                                <span className="text-fa-sky">{l.author?.name}</span>
                                <span className="border border-white/15 px-1.5 py-0.5">{T.rep} {l.author?.reputation ?? 0}</span>
                                {l.author_score && (
                                    <span className="text-fa-yellow inline-flex items-center gap-1"><Trophy size={10} /> {l.author_score.hits}/{l.author_score.total} {T.hit}</span>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </AppShell>
    );
}
